import { useCallback, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { SchemaSection } from '@rossum/api-client/schemas';
import { CopilotDialogV2 } from '@rossum/components/copilot-dialog/CopilotDialogV2';
import { SuggesterIconButton } from '@rossum/components/variable-suggestions/SuggesterIconButton';
import { SuggestVariables } from '@rossum/components/variable-suggestions/SuggestVariables';
import { Stack, Tooltip } from '@rossum/ui/material';
import { DatasetColumn } from '../../../../datasets/types';
import { SummaryBox } from '../../../../formulas/FormulaFieldSection/components/SummaryBox';
import { getFieldCompletions } from '../../../../formulas/getFieldCompletions';
import { baseSuggesterSections } from '../../../../formulas/suggesterSections';
import { handleInsert } from '../../../../rules/components/SuggestVariables/insertion-helpers';
import { PromptSuggestions } from './components/PromptSuggestions';
import { SelectDatasetChip } from './components/SelectDatasetChip';
import type { LookupResult } from './lookupResult';
import { useSuggestLookup } from './useSuggestLookup';

// Kept mounted so the target of the edit survives close and the title does not change mid-exit.
// That also means the prompt and the mutation outlive a session, so every close clears both.
export const LookupCopilot = ({
  open,
  onClose,
  onResult,
  fieldSchemaId,
  schemaContent,
  isIterative,
  queryIndex,
  summary,
  dataset,
  setDataset,
  datasetColumns,
}: {
  open: boolean;
  onClose: () => void;
  onResult: (result: LookupResult) => void;
  fieldSchemaId: string;
  schemaContent: SchemaSection[];
  isIterative?: boolean;
  queryIndex?: number | null;
  summary?: string;
  dataset: string;
  setDataset?: (dataset: string) => void;
  datasetColumns?: (DatasetColumn & { samples: string[] })[];
}) => {
  const intl = useIntl();
  const { mutate, isLoading, isError, reset } = useSuggestLookup({
    fieldSchemaId,
    schemaContent,
  });

  const [value, setValue] = useState('');
  const inputRef = useRef(null);

  // The dialog never unmounts, so a prompt or a failed attempt would otherwise be waiting for the
  // next session. Safe to clear here: closing is the last thing any path does.
  const handleClose = useCallback(() => {
    setValue('');
    reset();
    onClose();
  }, [onClose, reset]);

  const onSubmit = useCallback(
    (search: string) => {
      if (search) {
        mutate(
          {
            hint: search,
            queryIndex: isIterative ? (queryIndex ?? null) : null,
            dataset,
          },
          {
            onSuccess: result => {
              onResult(result);
              handleClose();
            },
          }
        );
      }
    },
    [dataset, handleClose, isIterative, mutate, onResult, queryIndex]
  );

  const sections = useMemo(() => {
    const base = baseSuggesterSections(intl, {
      fields: getFieldCompletions(schemaContent),
      dataset:
        datasetColumns?.map(column => ({
          value: column.datasetAttributeName,
          meta: 'column',
          description: column.samples.join(', '),
          caption: column.name,
          score: 1,
        })) ?? [],
    });
    return [
      base.dataset,
      base.fields,
      base.annotation,
      base.document,
      base.email,
    ];
  }, [intl, schemaContent, datasetColumns]);

  return (
    <CopilotDialogV2
      title={
        isIterative
          ? intl.formatMessage({
              id: 'features.queueSettings.fields.lookupCopilot.iterative.title',
            })
          : intl.formatMessage({
              id: 'features.queueSettings.fields.lookupCopilot.title',
            })
      }
      suggestingTitle={intl.formatMessage({
        id: 'features.queueSettings.fields.lookupCopilot.suggestingTitle',
      })}
      errorText={intl.formatMessage({
        id: 'features.queueSettings.fields.lookupCopilot.error',
      })}
      isSuggesting={isLoading}
      submitDisabled={!dataset || value.length === 0}
      submitDisabledTooltipTitle={intl.formatMessage({
        id: 'features.queueSettings.fields.lookupCopilot.copilotSubmitDisabled',
      })}
      onSubmit={onSubmit}
      onClose={handleClose}
      onSkip={handleClose}
      open={open}
      isError={isError}
      placeholder={intl.formatMessage({
        id: 'features.queueSettings.fields.lookupCopilot.placeholder',
      })}
      skipButtonLabel={intl.formatMessage({
        id: 'features.queueSettings.fields.lookupCopilot.skipButtonLabel',
      })}
      confirmText={
        isLoading
          ? intl.formatMessage({
              id: 'features.queueSettings.fields.lookupCopilot.confirm.inProgress',
            })
          : isIterative
            ? intl.formatMessage({
                id: 'features.queueSettings.fields.lookupCopilot.iterative.confirm',
              })
            : intl.formatMessage({
                id: 'features.queueSettings.fields.lookupCopilot.confirm',
              })
      }
      renderSummary={
        isIterative && summary ? <SummaryBox value={summary} /> : undefined
      }
      value={value}
      setValue={setValue}
      inputRef={inputRef}
      renderCopilotToolbar={
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          width="100%"
        >
          <SelectDatasetChip
            selectedDataset={dataset}
            setSelectedDataset={setDataset}
            disabled={isLoading}
            dataCy="copilot-dialog-select-dataset-chip"
          />
          <SuggestVariables
            sections={sections}
            mode="formula"
            onInsert={handleInsert({
              mode: 'textField',
              inputRef,
              currentValue: value,
              onChange: newValue => setValue(newValue),
            })}
          >
            <Tooltip
              title={intl.formatMessage({
                id: 'features.queueSettings.fields.lookupCopilot.copilotVariable',
              })}
              placement="top"
            >
              <span>
                <SuggesterIconButton
                  disabled={isLoading}
                  dataCy="copilot-dialog-suggester-icon-button"
                />
              </span>
            </Tooltip>
          </SuggestVariables>
        </Stack>
      }
      renderPromptSuggestions={
        <PromptSuggestions onPromptSelect={setValue} disabled={isLoading} />
      }
    />
  );
};
